import React, { useEffect, useMemo, useState } from 'react';
import withStyles from '@mui/styles/withStyles';
import styles from './TenantManagement.styles';
import Content from 'studio/components/Content/Content';
import DataGrid from 'studio/components/DataGrid/DataGrid';
import TextField from 'studio/components/TextField';
import { pluralize as plrl } from 'studio/helpers';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useMessage } from 'studio/components/Message';
import { useLoader } from 'components/loaders/FadeLoader';
import services from 'services';
import { TenantsIcon } from 'components/Icons';
import { trackEvent } from 'utils/eventTracking';
import _ from 'lodash';
import Link from 'studio/components/Link';
import { paths as studioPaths } from 'studio/App/Studio/Studio';
import { veezooUrls } from 'config/constants';
import { useConfirmationModal } from 'studio/components/ConfirmationModal/ConfirmationModal';
import PageTitle from 'studio/components/PageTitle';

// the free version of data grid limits the number of rows per page to at most 100
const pageSize = 100;

const tenantColumns = [
  { field: 'id', headerName: 'ID', flex: 1 },
  { field: 'name', headerName: 'Name', flex: 1 }
];

// Page that contains a table to create and delete tenants
const TenantManagement = ({ classes }) => {
  useEffect(() => {
    trackEvent('Admin Embedded Tenants Opened');
  }, []);

  const { successMessage, errorMessage } = useMessage();
  const { loading, withLoader } = useLoader();
  const { showConfirmationModal } = useConfirmationModal();

  // the tenants
  const [tenants, setTenants] = useState(null);
  // the ids of the tenants that are selected in the tenants table
  const [selectedTenantExternalIds, setSelectedTenantExternalIds] = useState([]);
  // specifies whether to show the dialog to create a new tenant
  const [showCreationDialog, setShowCreationDialog] = useState(false);
  // the value of the ID field in the dialog
  const [externalId, setExternalId] = useState('');
  // the value of the name field in the dialog
  // null means same as ID, which it is at the start, until it is explicitly modified
  const [name, setName] = useState(null);

  // the rows of the tenants table
  const tenantRows = useMemo(
    () =>
      tenants?.map(tenant => ({
        id: tenant.data.externalId,
        name: tenant.data.name
      })),
    [tenants]
  );
  // the number of selected tenants
  const nbSelectedTenants = selectedTenantExternalIds.length;

  // specifies if the "create" button in the dialog is enabled
  const isCreationEnabled = useMemo(
    () =>
      // ID is set
      externalId.length > 0 &&
      // Name is same as ID or not empty
      (name === null || name.length > 0) &&
      // and the creation request is not pending
      !loading,
    [externalId, name, loading]
  );

  // opens the creation dialog, resetting its state
  const openCreationDialog = () => {
    setName(null);
    setExternalId('');
    setShowCreationDialog(true);
  };

  const getTenants = async () => {
    const result = await withLoader(services.getTenants());
    if (result.success) {
      const newTenants = [...result.data.data];
      setTenants(newTenants);
      // Keep only selected tenant IDs that still exist
      setSelectedTenantExternalIds(selectedTenantExternalIds.filter(id => newTenants.includes(id)));
    } else {
      const message = result?.response?.data?.data?.message || 'There was an error getting the tenants';
      errorMessage(message);
    }
  };

  const createTenant = async (externalId, name) => {
    const body = { externalId, name: name || externalId };
    const result = await withLoader(services.createTenant(body));
    if (result.success) {
      successMessage(`The tenant has successfully been created`);
      setShowCreationDialog(false);
      // reload tenants
      await getTenants();
      return;
    }
    const message = `There was an error creating the tenant: ${result.response.data.data.message}`;
    errorMessage(message);
  };

  /**
   * Deletes tenants.
   */
  const deleteTenants = async externalIds => {
    // we don't show the internal IDs in the UI, so we need to map the external IDs to internal IDs for the API
    const ids = externalIds.map(id => tenants.find(tenant => tenant.data.externalId === id).data.id);
    // trigger the deletion request(s) and wait for all of them to complete
    const responses = await withLoader(Promise.all(ids.map(id => services.deleteTenant(id))));
    const [successResponses, errorResponses] = _.partition(responses, response => response.success);

    // show success and/or error messages
    if (successResponses.length > 0) {
      // show different messages for single / multiple deletions
      const message =
        externalIds.length === 1
          ? `The tenant has successfully been deleted`
          : `${successResponses.length} ${plrl(successResponses.length, 'tenant')} ${plrl(
              successResponses.length,
              'has',
              'have'
            )} successfully been deleted`;

      successMessage(message);
    }

    if (errorResponses.length > 0) {
      const responseData = errorResponses[0].data;
      // show different messages for single / multiple deletions
      const message =
        externalIds.length === 1
          ? `There was an error deleting the tenant${responseData.data ? `: ${responseData.data.message}` : ''}`
          : `${errorResponses.length} tenants failed to be deleted`;
      errorMessage(message);
    }

    // reload tenants
    await getTenants();
  };

  // retrieves the tenants
  useEffect(() => {
    getTenants();
  }, []);

  // the "create" action for the data grids
  const createAction = {
    label: 'Create',
    icon: <AddIcon />,
    tooltip: 'Create a new tenant',
    disabled: loading,
    onClick: openCreationDialog,
    'data-test': 'createTenantButton'
  };

  // the "delete" action for the data grids
  const deleteAction = {
    label: 'Delete',
    icon: <DeleteIcon />,
    tooltip:
      selectedTenantExternalIds.length > 0
        ? `Delete the selected ${plrl(selectedTenantExternalIds, 'tenant')}`
        : `Select one or more tenants to delete`,
    color: 'secondary',
    disabled: loading || selectedTenantExternalIds.length === 0,
    onClick: () =>
      showConfirmationModal(() => ({
        title: `Delete ${plrl(nbSelectedTenants, 'tenant')}`,
        content: (
          <>
            {nbSelectedTenants === 1
              ? `Do you really want to delete the tenant?`
              : `Do you really want to delete ${nbSelectedTenants} tenants?`}
            <p>
              All the users of {plrl(nbSelectedTenants, 'this tenant', 'these tenants')} will lose access to Veezoo.
            </p>
          </>
        ),
        confirmButtonText: 'Delete',
        onConfirm: async () => await deleteTenants(selectedTenantExternalIds)
      }))
  };

  return (
    <Content loading={!tenants} data-test="tenantsContent">
      <PageTitle icon={TenantsIcon}>Tenants</PageTitle>

      <Typography variant="body1" gutterBottom>
        Create and manage tenants, which usually correspond to your different customers. For embedded authentication,
        include the tenants&apos; IDs in the JWT or SAML assertion to identify the tenant of users. Refer to the{' '}
        <Link
          href={`${veezooUrls.docs}/embedded/embedded-guide/#4-configure-tenants-in-veezoo`}
          target="_blank"
          rel="noreferrer"
        >
          documentation
        </Link>{' '}
        for more information.
      </Typography>
      <Typography variant="body1" gutterBottom>
        To share access to a knowledge graph with the tenants, open the knowledge graph in{' '}
        <Link href={studioPaths.root} newWindow>
          Studio
        </Link>{' '}
        and create tenant-specific copies of it on the <i>Tenant Access</i> page.
      </Typography>

      <DataGrid
        className="tenantDatagrid"
        pageSize={pageSize}
        rows={tenantRows}
        columns={tenantColumns.map(column => ({ ...column, disableClickEventBubbling: true }))}
        checkboxSelection
        onSelectionModelChange={newSelection => setSelectedTenantExternalIds(newSelection)}
        sortModel={[{ field: 'id', sort: 'asc' }]}
        actions={[createAction, deleteAction]}
        noRowsText="There are no tenants"
      />

      <Dialog
        fullWidth
        maxWidth="sm"
        open={showCreationDialog}
        onClose={_ => setShowCreationDialog(false)}
        data-test="tenantCreationDialog"
      >
        <DialogTitle>Create tenant</DialogTitle>
        <DialogContent>
          Create a new tenant, which corresponds to a customer of yours.
          <TextField
            className={classes.inputField}
            value={externalId}
            label="ID"
            description="A unique identifier for the tenant, required to identify the tenant of users during JWT/SAML-based embedded authentication"
            onChange={event => setExternalId(event.target.value)}
            onKeyPress={async e => {
              if (e.key === 'Enter' && isCreationEnabled) {
                await createTenant(externalId, name);
              }
            }}
            autoFocus
            data-test="idTextfield"
          />
          <TextField
            className={classes.inputField}
            value={name === null ? externalId : name}
            label="Name"
            description="Name of the tenant"
            onChange={event => setName(event.target.value)}
            onKeyPress={async e => {
              if (e.key === 'Enter' && isCreationEnabled) {
                await createTenant(externalId, name);
              }
            }}
            data-test="nameTextfield"
          />
        </DialogContent>
        <DialogActions>
          <Button color="primary" disabled={loading} onClick={_ => setShowCreationDialog(false)}>
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={!isCreationEnabled}
            onClick={_ => createTenant(externalId, name)}
            data-test="createButton"
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Content>
  );
};

export default withStyles(styles)(TenantManagement);
